import * as yup from 'yup'
import type { FieldErrors, Resolver } from 'react-hook-form'

import type { AdditionalColumnIndex } from './bookingParameters'

export type ImportAdditionalColumnKey = `importAdditionalColumn${AdditionalColumnIndex}`
export type ExportAdditionalColumnKey = `exportAdditionalColumn${AdditionalColumnIndex}`

type AdditionalColumnFields = Record<ImportAdditionalColumnKey | ExportAdditionalColumnKey, string>

/** 荷物情報サブセクションが扱う固定カラム（tenant_parameters の columnName と対応） */
export const FIXED_CARGO_COLUMN_KEYS = [
  'importPackageType',
  'importMainGoods',
  'importGoodsName',
  'importGoodsAmount',
  'importGoodsOwner',
  'importContactNotes',
  'importDeliverySlipNumber',
  'exportPackageType',
  'exportMainGoods',
  'exportGoodsName',
  'exportGoodsAmount',
  'exportGoodsOwner',
  'exportContactNotes',
  'exportDeliverySlipNumber',
  'destination',
] as const
export type FixedCargoColumnKey = (typeof FIXED_CARGO_COLUMN_KEYS)[number]

type FixedCargoFields = Record<FixedCargoColumnKey, string>

/**
 * 添付ファイル（PC 互換）。
 * - 既存添付（サーバー登録済）は `id` を持ち、`content` を持たない
 * - 新規添付（フォーム上で追加）は `id` を持たず、`content` に base64 dataURL を持つ
 */
export type EditBookingAttachment = {
  id?: number
  name: string
  size: number
  lastModified?: number
  content?: string
}

export type EditBookingFormValues = {
  berthId: string
  startDate: string
  startTime: string
  endDate: string
  endTime: string
  adminNote: string
  companyName: string
  vehicleControllerName: string
  vehicleControllerPhone: string
  vehicleControllerEmail: string
  driverCompanyName: string
  vehicleNumber: string
  vehicleCategory: string
  driverName: string
  driverPhone: string
  driverEmail: string
  companyId: string | null
  driverCompanyId: string | null
  driverId: string | null
  vehicleControllerId: string | null
  // 荷物情報
  importFlag: boolean
  exportFlag: boolean
  // 入場指示時刻
  driverDateTime: string // YYYY-MM-DD
  driverCombinedTime: string // HH:mm
  skipInstructionTime: boolean
  // 添付ファイル（既存 + 未保存）
  attachments: EditBookingAttachment[]
  // 削除対象の既存添付 ID
  deleteAttachmentIds: number[]
} & FixedCargoFields &
  AdditionalColumnFields

export type EditBookingErrorField = keyof EditBookingFormValues | 'api'

export type EditBookingErrors = Partial<Record<EditBookingErrorField, string>>

export type EditBookingResolverContext = { isUnassigned: boolean }

export const ADMIN_NOTE_MAX_LENGTH = 255

const PHONE_REGEX = /^[0-9]{10,11}$/

const phoneSchema = yup
  .string()
  .test('phone', '電話番号は 10〜11 桁の数字で入力してください', (val) => {
    if (!val) return true
    return PHONE_REGEX.test(val.replace(/-/g, ''))
  })

export const editBookingSchema = yup
  .object({
    berthId: yup.string().when('$isUnassigned', {
      is: false,
      then: yup.string().required('バースを選択してください'),
      otherwise: yup.string(),
    }),
    startDate: yup.string().required('開始日を入力してください'),
    startTime: yup.string().required('開始時刻を入力してください'),
    endDate: yup.string().required('終了日を入力してください'),
    endTime: yup.string().required('終了時刻を入力してください'),
    adminNote: yup
      .string()
      .max(ADMIN_NOTE_MAX_LENGTH, `${ADMIN_NOTE_MAX_LENGTH}文字以内で入力してください`),
    vehicleControllerPhone: phoneSchema,
    // 配車担当者名が入力されているときメールアドレスを必須とする（PC 版互換）
    vehicleControllerEmail: yup
      .string()
      .email('メールアドレスの形式が正しくありません')
      .when('vehicleControllerName', {
        is: (val: string | undefined) => !!val,
        then: yup
          .string()
          .email('メールアドレスの形式が正しくありません')
          .required('メールアドレスを入力してください'),
      }),
    // ドライバー名が入力されているとき電話番号を必須とする（PC 版互換）
    driverPhone: yup.string().when('driverName', {
      is: (val: string | undefined) => !!val,
      then: phoneSchema.test('required', '電話番号を入力してください', (val) => !!val),
      otherwise: phoneSchema,
    }),
    driverEmail: yup.string().email('メールアドレスの形式が正しくありません'),
  })
  .test('entry-instruction', '', function (values) {
    const { driverDateTime, driverCombinedTime, skipInstructionTime } = (values ??
      {}) as Partial<EditBookingFormValues>
    if (skipInstructionTime) return true
    const hasDate = !!driverDateTime
    const hasTime = !!driverCombinedTime
    if (hasDate === hasTime) return true
    return this.createError({
      path: 'driverCombinedTime',
      message: '指示日と指示時間の両方を入力してください',
    })
  })
  .test('time-order', '', function (values) {
    const { startDate, startTime, endDate, endTime } = (values ??
      {}) as Partial<EditBookingFormValues>
    if (!startDate || !startTime || !endDate || !endTime) return true
    const start = `${startDate}T${startTime}`
    const end = `${endDate}T${endTime}`
    if (start < end) return true
    return this.createError({
      path: 'endTime',
      message:
        startDate === endDate
          ? '終了時刻は開始時刻より後にしてください'
          : '終了日時は開始日時より後にしてください',
    })
  })

/**
 * react-hook-form の Resolver。@hookform/resolvers のバージョン互換性を避けるため自作。
 * yup の ValidationError を RHF の errors 形式に変換する。
 */
export const editBookingResolver: Resolver<
  EditBookingFormValues,
  EditBookingResolverContext
> = async (values, context) => {
  try {
    await editBookingSchema.validate(values, {
      abortEarly: false,
      context: { isUnassigned: context?.isUnassigned ?? false },
    })
    return { values, errors: {} }
  } catch (err) {
    if (!(err instanceof yup.ValidationError)) {
      throw err
    }
    const errors: FieldErrors<EditBookingFormValues> = {}
    for (const issue of err.inner) {
      const path = issue.path as keyof EditBookingFormValues | undefined
      if (!path || errors[path]) continue
      errors[path] = { type: issue.type ?? 'validation', message: issue.message }
    }
    return { values: {}, errors }
  }
}
